import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
} from 'recharts';
import { AlertTriangle, UserX, Clock, X } from 'lucide-react';
import { analyticsAPI } from '../../services/api';

// ---------------------------------------------------------------------------
// Shared drill-down modal
// ---------------------------------------------------------------------------
const DrillModal = ({ title, columns, rows, loading, error, onClose, onBack, onRowClick, breadcrumb }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
    <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 flex flex-col max-h-[80vh]">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
        <div className="flex items-center gap-2 min-w-0">
          {onBack && (
            <button onClick={onBack}
              className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded flex-shrink-0">
              ← Back
            </button>
          )}
          <div className="min-w-0">
            {breadcrumb && breadcrumb.length > 1 && (
              <p className="text-xs text-gray-400 truncate mb-0.5">
                {breadcrumb.slice(0, -1).join(' › ')}
              </p>
            )}
            <h3 className="font-semibold text-gray-900 text-sm truncate">{title}</h3>
          </div>
        </div>
        <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 flex-shrink-0">
          <X className="h-5 w-5" />
        </button>
      </div>
      {onRowClick && !loading && !error && rows.length > 0 && (
        <p className="text-xs text-gray-400 px-5 py-2 border-b border-gray-100">Click a row to drill down</p>
      )}
      <div className="overflow-y-auto flex-1">
        {loading ? (
          <div className="py-12 text-center text-gray-400 text-sm">Loading…</div>
        ) : error ? (
          <div className="py-12 text-center text-red-500 text-sm px-6">{error}</div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-gray-400 text-sm">No records found.</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-100 text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                {columns.map(c => (
                  <th key={c.key} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map((r, i) => (
                <tr key={i}
                  className={onRowClick ? 'hover:bg-blue-50 cursor-pointer' : 'hover:bg-gray-50'}
                  onClick={onRowClick ? () => onRowClick(r) : undefined}>
                  {columns.map(c => (
                    <td key={c.key} className={`px-4 py-2 ${c.className || 'text-gray-700'}`}>
                      {r[c.key] ?? '—'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

const TABS = [
  { id: 'punctuality',  label: 'Punctuality',  icon: AlertTriangle },
  { id: 'absenteeism',  label: 'Absenteeism',  icon: UserX },
  { id: 'overtime',     label: 'Overtime',      icon: Clock },
];

const fmt = (d) => {
  const y  = d.getFullYear();
  const m  = String(d.getMonth() + 1).padStart(2, '0');
  const dy = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dy}`;
};

// Convert 'YYYY-MM' to { start: 'YYYY-MM-01', end: 'YYYY-MM-DD' } (last day of month)
const monthToRange = (month) => {
  const [y, m] = month.split('-').map(Number);
  return { start: `${month}-01`, end: fmt(new Date(y, m, 0)) };
};

const getPayrollCycle = () => {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth(); // 0-indexed
  if (today.getDate() >= 21) {
    // New cycle has started: 21st of this month → 20th of next month
    const start = new Date(y, m, 21);
    const end   = m === 11 ? new Date(y + 1, 0, 20) : new Date(y, m + 1, 20);
    return { start: fmt(start), end: fmt(end) };
  } else {
    // Still in current cycle: 21st of last month → 20th of this month
    const start = m === 0 ? new Date(y - 1, 11, 21) : new Date(y, m - 1, 21);
    const end   = new Date(y, m, 20);
    return { start: fmt(start), end: fmt(end) };
  }
};

const PRESETS = [
  { label: 'This Cycle', key: 'cycle' },
  { label: '3M',  key: '3m'  },
  { label: '6M',  key: '6m'  },
  { label: '12M', key: '12m' },
];

const computePreset = (key) => {
  if (key === 'cycle') return getPayrollCycle();
  const today = new Date();
  const n = key === '3m' ? 3 : key === '6m' ? 6 : 12;
  const start = new Date(today.getFullYear(), today.getMonth() - n, 1);
  return { start: fmt(start), end: fmt(today) };
};

const DateRangePicker = ({ onChange }) => {
  const defaultRange = getPayrollCycle();
  const [activePreset, setActivePreset] = useState('cycle');
  const [start, setStart] = useState(defaultRange.start);
  const [end,   setEnd]   = useState(defaultRange.end);
  const [dirty, setDirty] = useState(false); // custom dates not yet applied

  const selectPreset = (key) => {
    const range = computePreset(key);
    setActivePreset(key);
    setStart(range.start);
    setEnd(range.end);
    setDirty(false);
    onChange(range.start, range.end);
  };

  const handleDateChange = (field, val) => {
    if (field === 'start') setStart(val); else setEnd(val);
    setActivePreset(null);
    setDirty(true);
  };

  const applyCustom = () => {
    setDirty(false);
    onChange(start, end);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm">
        {PRESETS.map(p => (
          <button key={p.key} onClick={() => selectPreset(p.key)}
            className={`px-3 py-1.5 ${activePreset === p.key ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
            {p.label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-1 text-sm">
        <input type="date" value={start} onChange={e => handleDateChange('start', e.target.value)}
          className="border border-gray-300 rounded px-2 py-1 text-sm text-gray-700" />
        <span className="text-gray-400 text-xs">→</span>
        <input type="date" value={end} onChange={e => handleDateChange('end', e.target.value)}
          className="border border-gray-300 rounded px-2 py-1 text-sm text-gray-700" />
        {dirty && (
          <button onClick={applyCustom}
            className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm">
            Apply
          </button>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ label, value, sub, colour = 'text-gray-800' }) => (
  <div className="bg-white rounded-lg shadow p-4 text-center">
    <p className={`text-2xl font-bold ${colour}`}>{value}</p>
    <p className="text-xs text-gray-500 mt-1">{label}</p>
    {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
  </div>
);

const ErrorMsg = ({ msg }) => (
  <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{msg}</div>
);

const Empty = () => (
  <div className="bg-white rounded-lg shadow px-4 py-16 text-center text-gray-400 text-sm">
    No data available for this period.
  </div>
);

const CHART_VIEWS = [
  { key: 'trend', label: 'Trend' },
  { key: 'dept',  label: 'By Dept' },
  { key: 'user',  label: 'By User' },
];

// ---------------------------------------------------------------------------
// Punctuality tab
// ---------------------------------------------------------------------------
const PUNCT_DRILL_COLS = [
  { key: 'staff_no',   label: 'Staff No',    className: 'font-mono text-gray-600' },
  { key: 'name',       label: 'Name',         className: 'font-medium text-gray-900' },
  { key: 'late',       label: 'Late',         className: 'text-red-500 font-medium' },
  { key: 'early_out',  label: 'Early Out',    className: 'text-amber-500 font-medium' },
  // { key: 'total_days', label: 'Working Days', className: 'text-gray-500' },
];

const PunctualityTab = () => {
  const defaultRange                      = getPayrollCycle();
  const [start, setStart]                 = useState(defaultRange.start);
  const [end,   setEnd]                   = useState(defaultRange.end);
  const [data, setData]                   = useState(null);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState('');
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [chartView, setChartView]         = useState('trend');
  const [drill, setDrill]                 = useState({ open: false, title: '', rows: [], loading: false, error: '' });

  const fetchMain = useCallback(async () => {
    setLoading(true); setError(''); setSelectedMonth(null);
    try {
      const res = await analyticsAPI.getPunctuality(start, end);
      setData(res.data);
    } catch (e) { setError(e.response?.data?.details || 'Failed to load data.'); }
    finally     { setLoading(false); }
  }, [start, end]);

  useEffect(() => { fetchMain(); }, [fetchMain]);

  const handleRangeChange = (s, e) => { setStart(s); setEnd(e); };

  const openDrill = async (drillStart, drillEnd, dept, title) => {
    setDrill({ open: true, title, rows: [], loading: true, error: '' });
    try {
      const res = await analyticsAPI.getPunctualityDetail(drillStart, drillEnd, dept);
      setDrill(d => ({ ...d, rows: res.data.detail || [], loading: false }));
    } catch (e) {
      setDrill(d => ({ ...d, loading: false, error: e.response?.data?.details || 'Failed to load detail.' }));
    }
  };

  const totals   = data?.totals    || [];
  const by_dept  = data?.by_dept   || [];
  const topUsers = data?.top_users || [];

  const totalLate    = totals.reduce((s, r) => s + r.late,      0);
  const totalEarly   = totals.reduce((s, r) => s + r.early_out, 0);
  const totalRecords = totals.reduce((s, r) => s + r.total,     0);
  const latePct      = totalRecords ? ((totalLate / totalRecords) * 100).toFixed(1) : '0.0';

  const activeMonth = selectedMonth ?? data?.months?.slice(-1)[0] ?? '';
  const deptRows    = by_dept.filter(r => r.month === activeMonth);

  const allDeptTotals = Object.values(
    by_dept.reduce((acc, r) => {
      if (!acc[r.dept]) acc[r.dept] = { dept: r.dept, late: 0, early_out: 0, total: 0 };
      acc[r.dept].late      += r.late;
      acc[r.dept].early_out += r.early_out;
      acc[r.dept].total     += r.total;
      return acc;
    }, {})
  ).sort((a, b) => b.late - a.late);

  const deptChartHeight = Math.max(220, allDeptTotals.length * 36 + 40);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow p-4 flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-semibold text-gray-800">Punctuality Trends</h3>
        <DateRangePicker onChange={handleRangeChange} />
      </div>

      {error && <ErrorMsg msg={error} />}
      {loading && <div className="text-center py-10 text-gray-400 text-sm">Loading…</div>}

      {!loading && totals.length > 0 && (
        <>
          <div className="grid grid-cols-4 gap-4">
            <StatCard label="Late Arrivals"    value={totalLate}      colour="text-red-500" />
            <StatCard label="Early Departures" value={totalEarly}     colour="text-amber-500" />
            <StatCard label="Total Records"    value={totalRecords}   colour="text-gray-700" />
            <StatCard label="Late Rate"        value={`${latePct}%`} colour="text-red-400" sub={`${start} → ${end}`} />
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-gray-600">
                {chartView === 'trend' && 'Monthly Late & Early-Out Count'}
                {chartView === 'dept'  && 'Late & Early-Out by Department'}
                {chartView === 'user'  && 'Top Offenders'}
              </p>
              <div className="flex rounded-lg border border-gray-300 overflow-hidden text-xs">
                {CHART_VIEWS.map(v => (
                  <button key={v.key} onClick={() => setChartView(v.key)}
                    className={`px-3 py-1.5 ${chartView === v.key ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                    {v.label}
                  </button>
                ))}
              </div>
            </div>

            {chartView === 'trend' && (
              <>
                <p className="text-xs text-gray-400 mb-3">Click a bar to filter the department table below</p>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={totals} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="late" name="Late" radius={[3,3,0,0]} style={{ cursor: 'pointer' }}
                      onClick={d => setSelectedMonth(d.month)}>
                      {totals.map(t => (
                        <Cell key={t.month} fill={t.month === activeMonth ? '#DC2626' : '#EF4444'} />
                      ))}
                    </Bar>
                    <Bar dataKey="early_out" name="Early Out" radius={[3,3,0,0]} style={{ cursor: 'pointer' }}
                      onClick={d => setSelectedMonth(d.month)}>
                      {totals.map(t => (
                        <Cell key={t.month} fill={t.month === activeMonth ? '#D97706' : '#F59E0B'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </>
            )}

            {chartView === 'dept' && (
              <>
                <p className="text-xs text-gray-400 mb-3">Click a bar to see employees for that department</p>
                <ResponsiveContainer width="100%" height={deptChartHeight}>
                  <BarChart layout="vertical" data={allDeptTotals}
                    margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <YAxis type="category" dataKey="dept" tick={{ fontSize: 11 }} width={120} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="late" name="Late" fill="#EF4444" radius={[0,3,3,0]}
                      style={{ cursor: 'pointer' }}
                      onClick={r => openDrill(start, end, r.dept, `Punctuality — ${r.dept}`)} />
                    <Bar dataKey="early_out" name="Early Out" fill="#F59E0B" radius={[0,3,3,0]}
                      style={{ cursor: 'pointer' }}
                      onClick={r => openDrill(start, end, r.dept, `Punctuality — ${r.dept}`)} />
                  </BarChart>
                </ResponsiveContainer>
              </>
            )}

            {chartView === 'user' && (
              topUsers.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-100 text-sm mt-1">
                  <thead className="bg-gray-50">
                    <tr>
                      {['#', 'Staff No', 'Name', 'Department', 'Late', 'Early Out'].map(h => (
                        <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {topUsers.map((r, i) => (
                      <tr key={r.staff_no} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-gray-400 text-xs">{i + 1}</td>
                        <td className="px-4 py-2 font-mono text-gray-600">{r.staff_no}</td>
                        <td className="px-4 py-2 font-medium text-gray-900">{r.name}</td>
                        <td className="px-4 py-2 text-gray-500">{r.dept}</td>
                        <td className="px-4 py-2 text-red-500 font-medium">{r.late}</td>
                        <td className="px-4 py-2 text-amber-500 font-medium">{r.early_out}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="py-8 text-center text-gray-400 text-sm">No punctuality issues in this period.</div>
              )
            )}
          </div>

          {chartView === 'trend' && deptRows.length > 0 && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-sm font-medium text-gray-700">
                  Department Breakdown — <span className="text-blue-600">{activeMonth}</span>
                  <span className="ml-2 text-xs text-gray-400">Click a row to see employees</span>
                </p>
              </div>
              <table className="min-w-full divide-y divide-gray-100 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {['Department', 'Late', 'Early Out', 'Total Records', 'Late %'].map(h => (
                      <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {deptRows.map(r => {
                    const mr = monthToRange(activeMonth);
                    return (
                      <tr key={r.dept} className="hover:bg-blue-50 cursor-pointer"
                        onClick={() => openDrill(mr.start, mr.end, r.dept, `Punctuality — ${r.dept} — ${activeMonth}`)}>
                        <td className="px-4 py-2 font-medium text-blue-700 underline-offset-2 hover:underline">{r.dept}</td>
                        <td className="px-4 py-2 text-red-500 font-medium">{r.late}</td>
                        <td className="px-4 py-2 text-amber-500 font-medium">{r.early_out}</td>
                        <td className="px-4 py-2 text-gray-600">{r.total}</td>
                        <td className="px-4 py-2 text-gray-600">
                          {r.total ? ((r.late / r.total) * 100).toFixed(1) : '0.0'}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {!loading && totals.length === 0 && !error && <Empty />}

      {drill.open && (
        <DrillModal
          title={drill.title}
          columns={PUNCT_DRILL_COLS}
          rows={drill.rows}
          loading={drill.loading}
          error={drill.error}
          onClose={() => setDrill(d => ({ ...d, open: false }))}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Absenteeism tab
// ---------------------------------------------------------------------------
const ABSENT_DRILL_COLS = [
  { key: 'staff_no',    label: 'Staff No',    className: 'font-mono text-gray-600' },
  { key: 'name',        label: 'Name',         className: 'font-medium text-gray-900' },
  { key: 'absent_days', label: 'Absent Days',  className: 'text-red-500 font-medium' },
];

const AbsenteeismTab = () => {
  const defaultRange                      = getPayrollCycle();
  const [start, setStart]                 = useState(defaultRange.start);
  const [end,   setEnd]                   = useState(defaultRange.end);
  const [data, setData]                   = useState(null);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState('');
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [chartView, setChartView]         = useState('trend');
  const [drill, setDrill]                 = useState({ open: false, title: '', rows: [], loading: false, error: '' });

  const fetchMain = useCallback(async () => {
    setLoading(true); setError(''); setSelectedMonth(null);
    try {
      const res = await analyticsAPI.getAbsenteeism(start, end);
      setData(res.data);
    } catch (e) { setError(e.response?.data?.details || 'Failed to load data.'); }
    finally     { setLoading(false); }
  }, [start, end]);

  useEffect(() => { fetchMain(); }, [fetchMain]);

  const handleRangeChange = (s, e) => { setStart(s); setEnd(e); };

  const openDrill = async (drillStart, drillEnd, dept, title) => {
    setDrill({ open: true, title, rows: [], loading: true, error: '' });
    try {
      const res = await analyticsAPI.getAbsenteeismDetail(drillStart, drillEnd, dept);
      setDrill(d => ({ ...d, rows: res.data.detail || [], loading: false }));
    } catch (e) {
      setDrill(d => ({ ...d, loading: false, error: e.response?.data?.details || 'Failed to load detail.' }));
    }
  };

  const totals      = data?.totals       || [];
  const by_dept     = data?.by_dept      || [];
  const topUsers    = data?.top_users    || [];
  const pendingCount = data?.pending_count ?? 0;

  const totalAbsent = totals.reduce((s, r) => s + r.absent, 0);
  const activeMonth = selectedMonth ?? data?.months?.slice(-1)[0] ?? '';
  const deptRows    = by_dept.filter(r => r.month === activeMonth)
                             .sort((a, b) => b.absent - a.absent);

  const allDeptTotals = Object.values(
    by_dept.reduce((acc, r) => {
      if (!acc[r.dept]) acc[r.dept] = { dept: r.dept, absent: 0 };
      acc[r.dept].absent += r.absent;
      return acc;
    }, {})
  ).sort((a, b) => b.absent - a.absent);

  const deptChartHeight = Math.max(220, allDeptTotals.length * 36 + 40);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow p-4 flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-semibold text-gray-800">Absenteeism Trends</h3>
        <DateRangePicker onChange={handleRangeChange} />
      </div>

      {error && <ErrorMsg msg={error} />}
      {loading && <div className="text-center py-10 text-gray-400 text-sm">Loading…</div>}

      {!loading && (totals.length > 0 || pendingCount > 0) && (
        <>
          <div className="grid grid-cols-3 gap-4">
            <StatCard label="Absent Days"          value={totalAbsent}  colour="text-red-500" />
            <StatCard label="Avg / Month"          value={totals.length ? (totalAbsent / totals.length).toFixed(1) : '—'} colour="text-gray-700" />
            <StatCard label="Pending (Unresolved)" value={pendingCount} colour={pendingCount > 0 ? 'text-amber-500' : 'text-green-600'} sub="awaiting HR action" />
          </div>

          {totals.length > 0 && (
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-gray-600">
                  {chartView === 'trend' && 'Monthly Absent Count'}
                  {chartView === 'dept'  && 'Absent Days by Department'}
                  {chartView === 'user'  && 'Top Absentees'}
                </p>
                <div className="flex rounded-lg border border-gray-300 overflow-hidden text-xs">
                  {CHART_VIEWS.map(v => (
                    <button key={v.key} onClick={() => setChartView(v.key)}
                      className={`px-3 py-1.5 ${chartView === v.key ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>

              {chartView === 'trend' && (
                <>
                  <p className="text-xs text-gray-400 mb-3">Click a bar to filter the department table below</p>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={totals} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="absent" name="Absent Days" radius={[3,3,0,0]} style={{ cursor: 'pointer' }}
                        onClick={d => setSelectedMonth(d.month)}>
                        {totals.map(t => (
                          <Cell key={t.month} fill={t.month === activeMonth ? '#B91C1C' : '#EF4444'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </>
              )}

              {chartView === 'dept' && (
                <>
                  <p className="text-xs text-gray-400 mb-3">Click a bar to see employees for that department</p>
                  <ResponsiveContainer width="100%" height={deptChartHeight}>
                    <BarChart layout="vertical" data={allDeptTotals}
                      margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                      <XAxis type="number" tick={{ fontSize: 12 }} />
                      <YAxis type="category" dataKey="dept" tick={{ fontSize: 11 }} width={120} />
                      <Tooltip />
                      <Bar dataKey="absent" name="Absent Days" fill="#EF4444" radius={[0,3,3,0]}
                        style={{ cursor: 'pointer' }}
                        onClick={r => openDrill(start, end, r.dept, `Absenteeism — ${r.dept}`)} />
                    </BarChart>
                  </ResponsiveContainer>
                </>
              )}

              {chartView === 'user' && (
                topUsers.length > 0 ? (
                  <table className="min-w-full divide-y divide-gray-100 text-sm mt-1">
                    <thead className="bg-gray-50">
                      <tr>
                        {['#', 'Staff No', 'Name', 'Department', 'Absent Days'].map(h => (
                          <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {topUsers.map((r, i) => (
                        <tr key={r.staff_no} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-gray-400 text-xs">{i + 1}</td>
                          <td className="px-4 py-2 font-mono text-gray-600">{r.staff_no}</td>
                          <td className="px-4 py-2 font-medium text-gray-900">{r.name}</td>
                          <td className="px-4 py-2 text-gray-500">{r.dept}</td>
                          <td className="px-4 py-2 text-red-500 font-medium">{r.absent_days}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="py-8 text-center text-gray-400 text-sm">No absences in this period.</div>
                )
              )}
            </div>
          )}

          {chartView === 'trend' && deptRows.length > 0 && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-sm font-medium text-gray-700">
                  Department Breakdown — <span className="text-blue-600">{activeMonth}</span>
                  <span className="ml-2 text-xs text-gray-400">Click a row to see employees</span>
                </p>
              </div>
              <table className="min-w-full divide-y divide-gray-100 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {['Department', 'Absent Days'].map(h => (
                      <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {deptRows.map(r => {
                    const mr = monthToRange(activeMonth);
                    return (
                      <tr key={r.dept} className="hover:bg-blue-50 cursor-pointer"
                        onClick={() => openDrill(mr.start, mr.end, r.dept, `Absenteeism — ${r.dept} — ${activeMonth}`)}>
                        <td className="px-4 py-2 font-medium text-blue-700 underline-offset-2 hover:underline">{r.dept}</td>
                        <td className="px-4 py-2 text-red-500 font-medium">{r.absent}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {!loading && totals.length === 0 && pendingCount === 0 && !error && <Empty />}

      {drill.open && (
        <DrillModal
          title={drill.title}
          columns={ABSENT_DRILL_COLS}
          rows={drill.rows}
          loading={drill.loading}
          error={drill.error}
          onClose={() => setDrill(d => ({ ...d, open: false }))}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Overtime tab
// ---------------------------------------------------------------------------
const OT_DRILL_COLS = [
  { key: 'staff_no',    label: 'Staff No',     className: 'font-mono text-gray-600' },
  { key: 'name',        label: 'Name',          className: 'font-medium text-gray-900' },
  { key: 'weekday_hrs', label: 'Weekday hrs',   className: 'text-indigo-500 font-medium' },
  { key: 'weekend_hrs', label: 'Weekend hrs',   className: 'text-purple-500 font-medium' },
  { key: 'pubhol_hrs',  label: 'Public Hol hrs', className: 'text-amber-500 font-medium' },
  { key: 'total_hrs',   label: 'Total hrs',     className: 'text-blue-600 font-semibold' },
];

const OT_SUBDEPT_COLS = [
  { key: 'subdept',     label: 'Sub-Department', className: 'font-medium text-blue-700' },
  { key: 'weekday_hrs', label: 'Weekday hrs',    className: 'text-indigo-500 font-medium' },
  { key: 'weekend_hrs', label: 'Weekend hrs',    className: 'text-purple-500 font-medium' },
  { key: 'pubhol_hrs',  label: 'Public Hol hrs', className: 'text-amber-500 font-medium' },
  { key: 'total_hrs',   label: 'Total hrs',      className: 'text-blue-600 font-semibold' },
];

const OT_SECTION_COLS = [
  { key: 'section',     label: 'Section',        className: 'font-medium text-blue-700' },
  { key: 'weekday_hrs', label: 'Weekday hrs',    className: 'text-indigo-500 font-medium' },
  { key: 'weekend_hrs', label: 'Weekend hrs',    className: 'text-purple-500 font-medium' },
  { key: 'pubhol_hrs',  label: 'Public Hol hrs', className: 'text-amber-500 font-medium' },
  { key: 'total_hrs',   label: 'Total hrs',      className: 'text-blue-600 font-semibold' },
];

const OT_DAILY_COLS = [
  { key: 'date',        label: 'Date',           className: 'font-mono text-gray-700' },
  { key: 'day',         label: 'Day',            className: 'text-gray-500' },
  { key: 'weekday_hrs', label: 'Weekday hrs',    className: 'text-indigo-500 font-medium' },
  { key: 'weekend_hrs', label: 'Weekend hrs',    className: 'text-purple-500 font-medium' },
  { key: 'pubhol_hrs',  label: 'Public Hol hrs', className: 'text-amber-500 font-medium' },
  { key: 'total_hrs',   label: 'Total hrs',      className: 'text-blue-600 font-semibold' },
  { key: 'headcount',   label: 'Employees',      className: 'text-gray-500' },
];

const OT_DAY_EMP_COLS = [
  { key: 'staff_no',   label: 'Staff No',    className: 'font-mono text-gray-600' },
  { key: 'name',       label: 'Name',         className: 'font-medium text-gray-900' },
  { key: 'dept',       label: 'Department',   className: 'text-gray-500' },
  { key: 'total_hrs',  label: 'OT Hours',     className: 'text-blue-600 font-semibold' },
];

const OvertimeTab = () => {
  const defaultRange                      = getPayrollCycle();
  const [start, setStart]                 = useState(defaultRange.start);
  const [end,   setEnd]                   = useState(defaultRange.end);
  const [data, setData]                   = useState(null);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState('');
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [chartView, setChartView]         = useState('trend');
  const [drillStack, setDrillStack]       = useState([]);

  const fetchMain = useCallback(async () => {
    setLoading(true); setError(''); setSelectedMonth(null);
    try {
      const res = await analyticsAPI.getOvertime(start, end);
      setData(res.data);
    } catch (e) { setError(e.response?.data?.details || 'Failed to load data.'); }
    finally     { setLoading(false); }
  }, [start, end]);

  useEffect(() => { fetchMain(); }, [fetchMain]);

  const handleRangeChange = (s, e) => { setStart(s); setEnd(e); };

  // Stack helpers
  const pushFrame       = (frame) => setDrillStack(s => [...s, frame]);
  const popFrame        = ()      => setDrillStack(s => s.slice(0, -1));
  const closeDrill      = ()      => setDrillStack([]);
  const replaceTopFrame = (fn)    => setDrillStack(s =>
    s.length ? [...s.slice(0, -1), fn(s[s.length - 1])] : s
  );

  // Level 3 → employees (filtered by dept + subdept + section)
  const handleSectionClick = async (dept, subdept, section, rangeStart, rangeEnd) => {
    const label = section ?? '(No Section)';
    const initFrame = {
      type: 'employee', shortTitle: label, title: `${label} — Employees`,
      cols: OT_DRILL_COLS, rows: [], loading: true, error: '',
    };
    pushFrame(initFrame);
    try {
      const res = await analyticsAPI.getOvertimeDetail(rangeStart, rangeEnd, dept, subdept, section);
      replaceTopFrame(f => ({ ...f, rows: res.data.detail || [], loading: false }));
    } catch (e) {
      replaceTopFrame(f => ({ ...f, loading: false, error: e.response?.data?.details || 'Failed to load employees.' }));
    }
  };

  // Level 2 → sections (or skip to employees if no sections)
  const handleSubdeptClick = async (dept, subdept, rangeStart, rangeEnd) => {
    const label = subdept ?? '(No Sub-Dept)';
    const initFrame = {
      type: 'section', shortTitle: label, title: `${label} — Sections`,
      cols: OT_SECTION_COLS, rows: [], loading: true, error: '',
      dept, subdept, drillStart: rangeStart, drillEnd: rangeEnd,
    };
    pushFrame(initFrame);
    try {
      const res  = await analyticsAPI.getOvertimeSectionSummary(rangeStart, rangeEnd, dept, subdept);
      const rows = res.data.detail || [];
      const hasSections = rows.some(r => r.section != null);
      if (!hasSections) {
        // No sections — skip straight to employees
        replaceTopFrame(f => ({ ...f, title: `${label} — Employees`, cols: OT_DRILL_COLS, type: 'employee', rows: [], loading: true }));
        try {
          const empRes = await analyticsAPI.getOvertimeDetail(rangeStart, rangeEnd, dept, subdept);
          replaceTopFrame(f => ({ ...f, rows: empRes.data.detail || [], loading: false }));
        } catch (e2) {
          replaceTopFrame(f => ({ ...f, loading: false, error: e2.response?.data?.details || 'Failed to load employees.' }));
        }
      } else {
        replaceTopFrame(f => ({ ...f, rows, loading: false }));
      }
    } catch (e) {
      replaceTopFrame(f => ({ ...f, loading: false, error: e.response?.data?.details || 'Failed to load sections.' }));
    }
  };

  // Level 1 → subdepts (or skip to employees if no subdepts)
  const handleDeptClick = async (dept, rangeStart, rangeEnd) => {
    const initFrame = {
      type: 'subdept', shortTitle: dept, title: `${dept} — Sub-Departments`,
      cols: OT_SUBDEPT_COLS, rows: [], loading: true, error: '',
      dept, drillStart: rangeStart, drillEnd: rangeEnd,
    };
    setDrillStack([initFrame]);
    try {
      const res  = await analyticsAPI.getOvertimeSubdeptSummary(rangeStart, rangeEnd, dept);
      const rows = res.data.detail || [];
      const hasSubdepts = rows.some(r => r.subdept != null);
      if (!hasSubdepts) {
        // No subdepts — skip straight to employees
        setDrillStack([{ ...initFrame, title: `${dept} — Employees`, cols: OT_DRILL_COLS, type: 'employee', rows: [], loading: true }]);
        try {
          const empRes = await analyticsAPI.getOvertimeDetail(rangeStart, rangeEnd, dept);
          setDrillStack(s => s.length ? [{ ...s[0], rows: empRes.data.detail || [], loading: false }] : s);
        } catch (e2) {
          setDrillStack(s => s.length ? [{ ...s[0], loading: false, error: e2.response?.data?.details || 'Failed to load employees.' }] : s);
        }
      } else {
        setDrillStack([{ ...initFrame, rows, loading: false }]);
      }
    } catch (e) {
      setDrillStack([{ ...initFrame, loading: false, error: e.response?.data?.details || 'Failed to load sub-departments.' }]);
    }
  };

  // Day → employees for that single day (all depts)
  const handleDayClick = async (dateStr) => {
    const initFrame = {
      type: 'employee', shortTitle: dateStr, title: `${dateStr} — Employees`,
      cols: OT_DAY_EMP_COLS, rows: [], loading: true, error: '',
    };
    pushFrame(initFrame);
    try {
      const res = await analyticsAPI.getOvertimeDetail(dateStr, dateStr);
      replaceTopFrame(f => ({ ...f, rows: res.data.detail || [], loading: false }));
    } catch (e) {
      replaceTopFrame(f => ({ ...f, loading: false, error: e.response?.data?.details || 'Failed to load employees.' }));
    }
  };

  // Month bar click → daily breakdown modal (also updates dept table selection)
  const handleMonthClick = async (month) => {
    setSelectedMonth(month);
    const range = monthToRange(month);
    const initFrame = {
      type: 'daily', shortTitle: month, title: `${month} — Daily Breakdown`,
      cols: OT_DAILY_COLS, rows: [], loading: true, error: '',
      drillStart: range.start, drillEnd: range.end,
    };
    setDrillStack([initFrame]);
    try {
      const res = await analyticsAPI.getOvertimeDaily(range.start, range.end);
      setDrillStack([{ ...initFrame, rows: res.data.detail || [], loading: false }]);
    } catch (e) {
      setDrillStack([{ ...initFrame, loading: false, error: e.response?.data?.details || 'Failed to load daily data.' }]);
    }
  };

  // Called when a row is clicked inside the drill modal
  const handleDrillRowClick = (row) => {
    const frame = drillStack[drillStack.length - 1];
    if (!frame) return;
    if (frame.type === 'subdept') {
      handleSubdeptClick(frame.dept, row.subdept, frame.drillStart, frame.drillEnd);
    } else if (frame.type === 'section') {
      handleSectionClick(frame.dept, frame.subdept, row.section, frame.drillStart, frame.drillEnd);
    } else if (frame.type === 'daily') {
      handleDayClick(row.date);
    }
  };

  const monthly  = data?.monthly   || [];
  const topUsers = data?.top_users || [];
  const by_dept  = data?.by_dept   || [];

  const totalHrs   = monthly.reduce((s, r) => s + r.weekday_hrs + r.weekend_hrs + r.pubhol_hrs, 0).toFixed(1);
  const weekdayHrs = monthly.reduce((s, r) => s + r.weekday_hrs, 0).toFixed(1);
  const weekendHrs = monthly.reduce((s, r) => s + r.weekend_hrs + r.pubhol_hrs, 0).toFixed(1);

  const activeMonth = selectedMonth ?? data?.months?.slice(-1)[0] ?? '';
  const deptRows    = by_dept.filter(r => r.month === activeMonth);

  const allDeptTotals = Object.values(
    by_dept.reduce((acc, r) => {
      if (!acc[r.dept]) acc[r.dept] = { dept: r.dept, weekday_hrs: 0, weekend_hrs: 0, pubhol_hrs: 0 };
      acc[r.dept].weekday_hrs += r.weekday_hrs;
      acc[r.dept].weekend_hrs += r.weekend_hrs;
      acc[r.dept].pubhol_hrs  += r.pubhol_hrs;
      return acc;
    }, {})
  ).sort((a, b) => (b.weekday_hrs + b.weekend_hrs + b.pubhol_hrs) - (a.weekday_hrs + a.weekend_hrs + a.pubhol_hrs));

  const deptChartHeight = Math.max(220, allDeptTotals.length * 36 + 40);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow p-4 flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-semibold text-gray-800">Overtime Trends</h3>
        <DateRangePicker onChange={handleRangeChange} />
      </div>

      {error && <ErrorMsg msg={error} />}
      {loading && <div className="text-center py-10 text-gray-400 text-sm">Loading…</div>}

      {!loading && monthly.length > 0 && (
        <>
          <div className="grid grid-cols-3 gap-4">
            <StatCard label="Total OT Hours"        value={`${totalHrs}h`}   colour="text-blue-600" />
            <StatCard label="Weekday OT Hours"       value={`${weekdayHrs}h`} colour="text-indigo-500" />
            <StatCard label="Weekend / PH OT Hours"  value={`${weekendHrs}h`} colour="text-purple-500" />
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-gray-600">
                {chartView === 'trend' && 'Monthly OT Hours (Stacked)'}
                {chartView === 'dept'  && 'OT Hours by Department'}
                {chartView === 'user'  && 'Top OT Contributors'}
              </p>
              <div className="flex rounded-lg border border-gray-300 overflow-hidden text-xs">
                {CHART_VIEWS.map(v => (
                  <button key={v.key} onClick={() => setChartView(v.key)}
                    className={`px-3 py-1.5 ${chartView === v.key ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                    {v.label}
                  </button>
                ))}
              </div>
            </div>

            {chartView === 'trend' && (
              <>
                <p className="text-xs text-gray-400 mb-3">Click a bar to see the daily breakdown for that month</p>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={monthly} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} unit="h" />
                    <Tooltip formatter={(v) => `${v}h`} />
                    <Legend />
                    <Bar dataKey="weekday_hrs" name="Weekday"        stackId="a" fill="#3B82F6" radius={[0,0,0,0]}
                      style={{ cursor: 'pointer' }} onClick={d => handleMonthClick(d.month)} />
                    <Bar dataKey="weekend_hrs" name="Weekend"        stackId="a" fill="#8B5CF6" radius={[0,0,0,0]}
                      style={{ cursor: 'pointer' }} onClick={d => handleMonthClick(d.month)} />
                    <Bar dataKey="pubhol_hrs"  name="Public Holiday" stackId="a" fill="#F59E0B" radius={[3,3,0,0]}
                      style={{ cursor: 'pointer' }} onClick={d => handleMonthClick(d.month)} />
                  </BarChart>
                </ResponsiveContainer>
              </>
            )}

            {chartView === 'dept' && (
              <>
                <p className="text-xs text-gray-400 mb-3">Click a bar to drill into that department</p>
                <ResponsiveContainer width="100%" height={deptChartHeight}>
                  <BarChart layout="vertical" data={allDeptTotals}
                    margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                    <XAxis type="number" tick={{ fontSize: 12 }} unit="h" />
                    <YAxis type="category" dataKey="dept" tick={{ fontSize: 11 }} width={120} />
                    <Tooltip formatter={(v) => `${v}h`} />
                    <Legend />
                    <Bar dataKey="weekday_hrs" name="Weekday"        stackId="a" fill="#3B82F6" radius={[0,0,0,0]}
                      style={{ cursor: 'pointer' }}
                      onClick={r => handleDeptClick(r.dept, start, end)} />
                    <Bar dataKey="weekend_hrs" name="Weekend"        stackId="a" fill="#8B5CF6" radius={[0,0,0,0]}
                      style={{ cursor: 'pointer' }}
                      onClick={r => handleDeptClick(r.dept, start, end)} />
                    <Bar dataKey="pubhol_hrs"  name="Public Holiday" stackId="a" fill="#F59E0B" radius={[0,3,3,0]}
                      style={{ cursor: 'pointer' }}
                      onClick={r => handleDeptClick(r.dept, start, end)} />
                  </BarChart>
                </ResponsiveContainer>
              </>
            )}

            {chartView === 'user' && (
              topUsers.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-100 text-sm mt-1">
                  <thead className="bg-gray-50">
                    <tr>
                      {['#', 'Staff No', 'Name', 'Department', 'Total OT Hours'].map(h => (
                        <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {topUsers.map((r, i) => (
                      <tr key={r.staff_no} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-gray-400 text-xs">{i + 1}</td>
                        <td className="px-4 py-2 font-mono text-gray-600">{r.staff_no}</td>
                        <td className="px-4 py-2 font-medium text-gray-900">{r.name}</td>
                        <td className="px-4 py-2 text-gray-500">{r.dept}</td>
                        <td className="px-4 py-2 text-blue-600 font-semibold">{r.total_hrs}h</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="py-8 text-center text-gray-400 text-sm">No approved overtime in this period.</div>
              )
            )}
          </div>

          {chartView === 'trend' && allDeptTotals.length > 0 && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-sm font-medium text-gray-700">
                  Department Breakdown — <span className="text-blue-600">{start} to {end}</span>
                  <span className="ml-2 text-xs text-gray-400">Click a row to see employees</span>
                </p>
              </div>
              <table className="min-w-full divide-y divide-gray-100 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {['Department', 'Weekday', 'Weekend', 'Public Hol', 'Total'].map(h => (
                      <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {allDeptTotals.map(r => {
                    const total = (r.weekday_hrs + r.weekend_hrs + r.pubhol_hrs).toFixed(1);
                    return (
                      <tr key={r.dept} className="hover:bg-blue-50 cursor-pointer"
                        onClick={() => handleDeptClick(r.dept, start, end)}>
                        <td className="px-4 py-2 font-medium text-blue-700 underline-offset-2 hover:underline">{r.dept}</td>
                        <td className="px-4 py-2 text-indigo-500 font-medium">{r.weekday_hrs.toFixed(1)}h</td>
                        <td className="px-4 py-2 text-purple-500 font-medium">{r.weekend_hrs.toFixed(1)}h</td>
                        <td className="px-4 py-2 text-amber-500 font-medium">{r.pubhol_hrs.toFixed(1)}h</td>
                        <td className="px-4 py-2 text-blue-600 font-semibold">{total}h</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {!loading && monthly.length === 0 && !error && <Empty />}

      {drillStack.length > 0 && (() => {
        const frame = drillStack[drillStack.length - 1];
        return (
          <DrillModal
            title={frame.title}
            columns={frame.cols}
            rows={frame.rows}
            loading={frame.loading}
            error={frame.error}
            breadcrumb={drillStack.map(f => f.shortTitle)}
            onBack={drillStack.length > 1 ? popFrame : null}
            onClose={closeDrill}
            onRowClick={['subdept', 'section', 'daily'].includes(frame.type) ? handleDrillRowClick : null}
          />
        );
      })()}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Panel shell
// ---------------------------------------------------------------------------
const AnalyticsPanel = () => {
  const [activeTab, setActiveTab] = useState('punctuality');

  return (
    <div className="space-y-6 max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
      </div>

      <div className="border-b border-gray-200 bg-white shadow-sm rounded-t-lg px-4 pt-2">
        <nav className="-mb-px flex space-x-8">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`${
                activeTab === id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <Icon className="h-5 w-5 mr-2" />
              {label}
            </button>
          ))}
        </nav>
      </div>

      <div className="mt-6">
        {activeTab === 'punctuality' && <PunctualityTab />}
        {activeTab === 'absenteeism' && <AbsenteeismTab />}
        {activeTab === 'overtime'    && <OvertimeTab />}
      </div>
    </div>
  );
};

export default AnalyticsPanel;
